import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/utils';
import ReactDOM from 'react-dom/client';
import { CloseFilledIcon } from '../Icons';
import { FeatureFlagProvider } from '@jenmusic/feature-flag';
import { FEATURE_FLAG_CONFIG } from '@/config';

type DialogProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  info?: (options: {
    title?: string;
    description?: string;
    confirmLabel?: string;
    inputLabel?: string;
    defaultValue?: string;
    dialogContentClassName?: string;
    overlayClassName?: string;
    closeClassName?: string;
    canNotCloseOnMask?: boolean;
    contentJSX?: React.JSXElementConstructor<{
      onChange: (val: any) => void;
      onClose: (result?: string | null) => void;
    }>;
    footerJSX?: React.JSXElementConstructor<any>;
    container?: HTMLElement;
  }) => Promise<any>;
  basic?: (options: {
    closeClassName?: string;
    closeColor?: string;
    dialogContentClassName?: string;
    contentJSX?: React.JSXElementConstructor<{
      onChange?: (val: any) => void;
      onClose: (result?: string | null) => void;
    }>;
  }) => Promise<any>;
  confirm?: (options: {
    title: string;
    description?: string;
    successWord: string;
    cancelWord?: string;
    dialogContentClassName?: string;
    overlayClassName?: string;
    closeClassName?: string;
    canNotCloseOnMask?: boolean;
    container?: HTMLElement;
  }) => Promise<boolean>;
};

const Dialog: React.FC<DialogProps> & {
  info: NonNullable<DialogProps['info']>;
  basic: NonNullable<DialogProps['basic']>;
  confirm: NonNullable<DialogProps['confirm']>;
} = (props) => {
  return <DialogPrimitive.Root {...props} />;
};

Dialog.info = (options) => {
  return new Promise((resolve) => {
    const div = document.createElement('div');
    document.body.appendChild(div);

    const root = ReactDOM.createRoot(div);

    const handleClose = (result?: string | null) => {
      root.unmount();
      document.body.removeChild(div);
      resolve(result);
    };

    const DialogComponent = () => {
      const [inputValue, setInputValue] = React.useState(options.defaultValue || '');

      return (
        <FeatureFlagProvider featureFlagConfig={FEATURE_FLAG_CONFIG}>
          <Dialog open={true} onOpenChange={() => !options.canNotCloseOnMask && handleClose(null)}>
            <DialogTitle />
            <DialogContent
              closeClassName={options.closeClassName}
              className={cn('max-w-[calc(100vw-16px)] w-[400px]', options.dialogContentClassName)}
              container={options.container}
            >
              {
                <DialogHeader
                  className={cn('p-jen-spacing-16', {
                    hidden: !options.title,
                  })}
                >
                  <DialogTitle className={cn('flex items-center h-[24px] box-border text-jen-size-heading-04')}>
                    {options.title}
                  </DialogTitle>
                </DialogHeader>
              }

              <div
                className={cn({
                  'border-jen-border border-b': Boolean(options.title),
                })}
              ></div>
              {options.description && <DialogDescription>{options.description}</DialogDescription>}
              {options.inputLabel && (
                <div className="my-4 ">
                  <label>{options.inputLabel}</label>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="w-full p-2 border rounded text-black"
                  />
                </div>
              )}
              {options.contentJSX && <options.contentJSX onClose={handleClose} onChange={(val) => setInputValue(val)} />}
              <div className=" absolute left-0 bottom-0 w-full ">
                <div
                  className={cn({
                    'border-jen-border border-b': Boolean(options.footerJSX),
                  })}
                ></div>
                {options.footerJSX && <options.footerJSX />}
              </div>
            </DialogContent>
            <DialogDescription />
          </Dialog>
        </FeatureFlagProvider>
      );
    };

    root.render(<DialogComponent />);
  });
};

Dialog.basic = (options) => {
  return new Promise((resolve) => {
    const div = document.createElement('div');
    document.body.appendChild(div);

    const handleClose = (result?: string | null) => {
      root.unmount();
      document.body.removeChild(div);
      resolve(result);
    };

    const DialogComponent = () => {
      return (
        <Dialog open={true} onOpenChange={() => handleClose(null)}>
          <DialogOverlay />
          <DialogContent
            closeClassName={options.closeClassName}
            closeColor={options.closeColor}
            className={cn('max-w-[calc(100vw-16px)] w-[400px]', options.dialogContentClassName)}
            aria-description="dialog content"
          >
            <DialogTitle className="hidden" />
            <DialogDescription className="hidden" />
            {options.contentJSX && <options.contentJSX onClose={handleClose} />}
          </DialogContent>
        </Dialog>
      );
    };

    const root = ReactDOM.createRoot(div);
    root.render(<DialogComponent />);
  });
};

Dialog.confirm = (options) => {
  return new Promise((resolve) => {
    const div = document.createElement('div');
    document.body.appendChild(div);

    const root = ReactDOM.createRoot(div);

    const handleClose = () => {
      root.unmount();
      document.body.removeChild(div);
    };

    const DialogComponent = () => {
      return (
        <FeatureFlagProvider featureFlagConfig={FEATURE_FLAG_CONFIG}>
          <Dialog open={true} onOpenChange={() => !options.canNotCloseOnMask && handleClose()}>
            <DialogOverlay />
            <DialogContent
              closeClassName={`w-[24px] h-[24px] !p-0 ${options.closeClassName} `}
              className={cn('max-w-[calc(100vw-16px)] w-[400px]', options.dialogContentClassName)}
              container={options.container}
            >
              <DialogHeader className="p-jen-spacing-16">
                <DialogTitle className="flex items-center h-[24px] box-border text-jen-size-heading-04">{options.title}</DialogTitle>
              </DialogHeader>

              <div className="border-jen-border border-b"></div>

              {options.description && (
                <div className="px-jen-spacing-16 py-jen-spacing-12 text-jen-content-secondary">{options.description}</div>
              )}

              <div className="px-jen-spacing-16 py-jen-spacing-12 flex justify-end gap-2">
                {options.cancelWord && (
                  <button
                    onClick={() => {
                      handleClose();
                    }}
                    className=" cursor-pointer px-4 py-2 rounded-md border border-jen-border text-jen-content-primary hover:bg-jen-background-secondary"
                  >
                    {options.cancelWord}
                  </button>
                )}
                <button
                  onClick={() => {
                    handleClose();
                    resolve(true);
                  }}
                  className="px-4 py-2 cursor-pointer rounded-md bg-jenmusic-interactive text-white hover:opacity-90"
                >
                  {options.successWord}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </FeatureFlagProvider>
      );
    };

    root.render(<DialogComponent />);
  });
};

const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    disableMaskClose?: boolean;
  }
>(({ className, disableMaskClose, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    onClick={(event) => {
      if (disableMaskClose) {
        event.preventDefault();
      }
    }}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    closeClassName?: string;
    closeColor?: string;
    closePosition?: 'left' | 'right';
    closeStyle?: React.CSSProperties;
    container?: HTMLElement;
  }
>(({ className, children, closeClassName, closeColor, closePosition = 'right', closeStyle, container, ...props }, ref) => {
  return (
    <DialogPortal container={container}>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-[16px]',
          className,
        )}
        onWheel={(e) => {
          e.stopPropagation();
        }}
        {...props}
      >
        <div className="">
          <DialogPrimitive.Close
            className={cn(
              'absolute top-4 z-20 bg-[#CECFD3] p-jen-spacing-8 rounded-jen-spacing-8',
              closePosition === 'left' ? 'left-4' : 'right-4',
              closeClassName,
            )}
            style={closeStyle}
          >
            <CloseFilledIcon fillColor={closeColor} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
          <div className="max-h-[80vh] overflow-y-auto">{children}</div>
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, children, ...props }, ref) => {
  if (!children) return null;

  return (
    <DialogPrimitive.Title ref={ref} className={cn('text-jen-content-primary text-lg leading-none tracking-tight', className)} {...props}>
      {children}
    </DialogPrimitive.Title>
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
